'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useI18n, useTheme, Lang } from '@/i18n';
import { authApi, isLoggedIn, clearTokens, getRefreshToken } from '@/lib/api';
import TwoFaModal from '@/components/TwoFaModal';

interface NavItem {
  key: string;
  path: string;
  icon: string;
}

const navSections: { titleKey: string; items: NavItem[] }[] = [
  {
    titleKey: 'nav.overview',
    items: [{ key: 'nav.dashboard', path: '/dashboard', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' }],
  },
  {
    titleKey: 'nav.business',
    items: [
      { key: 'nav.assets', path: '/assets', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
      { key: 'nav.data', path: '/data', icon: 'M3 13h4v8H3zM10 5h4v16h-4zM17 9h4v12h-4z' },
      { key: 'nav.projects', path: '/projects', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
      { key: 'nav.orders', path: '/orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a1 1 0 001 1h4a1 1 0 001-1M9 5a1 1 0 000 1h4a1 1 0 001-1m-6 9h6m-6 4h6' },
      { key: 'nav.equity', path: '/equity', icon: 'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z' },
    ],
  },
  {
    titleKey: 'nav.finance',
    items: [
      { key: 'nav.funds', path: '/funds', icon: 'M3 10h18M7 15h2m4 0h4m-9-5l2-3m4 3l-2-3m-6 11h12a2 2 0 002-2V8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z' },
      { key: 'nav.revenue', path: '/revenue', icon: 'M9 7h6m0 10v-2m-6 2v-2m6-6h-2m-2 0h-2m8-6H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z' },
      { key: 'nav.financeReports', path: '/finance', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    ],
  },
  {
    titleKey: 'nav.compliance',
    items: [
      { key: 'nav.investors', path: '/investors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
      { key: 'nav.kyc', path: '/kyc', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
      { key: 'nav.channel', path: '/channel', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
      { key: 'nav.contracts', path: '/contracts', icon: 'M8 7h8m-8 4h8m-8 4h4m-9-9v14a1 1 0 001 1h10a1 1 0 001-1V5a1 1 0 00-1-1H3a1 1 0 00-1 1zm14 0h2a1 1 0 011 1v12a1 1 0 01-1 1h-2' },
    ],
  },
  {
    titleKey: 'nav.system',
    items: [
      { key: 'nav.audit', path: '/audit', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
      { key: 'nav.reports', path: '/reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
      { key: 'nav.announcements', path: '/announcements', icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z' },
      { key: 'nav.settings', path: '/settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94 1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z' },
    ],
  },
];

export default function AdminShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [notifications] = useState(5);
  const [ready, setReady] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [user, setUser] = useState<{ email: string; name?: string | null; role?: { name: string } | null } | null>(null);

  // 路由守卫：无 token 跳登录；拉取当前管理员信息
  useEffect(() => {
    if (!isLoggedIn()) {
      router.replace('/login');
      return;
    }
    authApi
      .me()
      .then((u) => setUser(u))
      .catch(() => {
        clearTokens();
        router.replace('/login');
      })
      .finally(() => setReady(true));
  }, [router]);

  const handleLogout = () => {
    const rt = getRefreshToken();
    if (rt) authApi.logout(rt).catch(() => undefined);
    clearTokens();
    router.replace('/login');
  };

  if (!ready) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg">
        <div className="text-muted text-sm">{t('nav.loading')}</div>
      </div>
    );
  }

  const isActive = (path: string) => pathname === path || pathname?.startsWith(path + '/');

  const switchLang = (l: Lang) => {
    setLang(l);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* ===== 侧边栏 ===== */}
      <aside
        className={`${collapsed ? 'w-[68px]' : 'w-64'} flex-shrink-0 bg-gradient-to-b from-sidebar1 to-sidebar2 border-r border-black/20 flex flex-col transition-all duration-300`}
      >
        <div className="p-4 border-b border-white/10 bg-gradient-to-r from-maple/15 to-transparent">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-white shadow">
              <Image src="/logo.png" alt="WeeMaple" width={44} height={44} className="w-full h-full object-contain" priority />
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <div className="font-bold text-white text-sm whitespace-nowrap">Maple Nexus</div>
                <div className="text-[11px] text-maple-light whitespace-nowrap">{t('nav.console')}</div>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.titleKey}>
              {!collapsed && (
                <div className="text-[11px] font-semibold text-maple-light/90 uppercase tracking-wider px-3 mb-2">{t(section.titleKey)}</div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const activeItem = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      href={item.path}
                      prefetch={false}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                        activeItem
                          ? 'bg-gradient-to-r from-maple/30 to-maple/10 text-white font-semibold shadow-inner border-l-2 border-maple'
                          : 'text-neutral-300 hover:bg-white/15 hover:text-white'
                      } ${collapsed ? 'justify-center' : ''}`}
                      title={collapsed ? t(item.key) : undefined}
                    >
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                      </svg>
                      {!collapsed && <span className="text-sm whitespace-nowrap">{t(item.key)}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-neutral-400 hover:bg-white/10 hover:text-white transition-all"
          >
            <svg className={`w-5 h-5 transition-transform ${!collapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!collapsed && <span className="text-sm">{t('nav.collapse')}</span>}
          </button>
        </div>
      </aside>

      {/* ===== 主区域 ===== */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6 flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="relative">
              <svg className="w-5 h-5 text-maple-text absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('nav.searchPlaceholder')}
                className="pl-10 pr-4 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted focus:border-maple focus:ring-1 focus:ring-maple outline-none w-64 md:w-80"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* 语言切换 */}
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => switchLang('en')}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${lang === 'en' ? 'bg-maple text-neutral-900' : 'text-secondary hover:bg-hover'}`}
              >
                EN
              </button>
              <button
                onClick={() => switchLang('zh')}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${lang === 'zh' ? 'bg-maple text-neutral-900' : 'text-secondary hover:bg-hover'}`}
              >
                中文
              </button>
            </div>

            {/* 主题切换 */}
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-hover transition-colors border border-transparent hover:border-border" title={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}>
              {theme === 'dark' ? (
                <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            <button className="relative p-2 rounded-lg hover:bg-hover transition-colors border border-transparent hover:border-border">
              <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger rounded-full"></span>
            </button>
            <div className="flex items-center gap-3 pl-4 border-l border-border">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-white shadow-md">
                <Image src="/logo.png" alt="Admin" width={44} height={44} className="w-full h-full object-contain" />
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-semibold text-foreground">{user?.name || user?.email || 'Admin'}</div>
                <div className="text-[11px] font-medium text-maple-text">{user?.role?.name || 'Super Admin'}</div>
              </div>
              <button
                onClick={() => setTwoFaOpen(true)}
                className="p-2 rounded-lg hover:bg-hover transition-colors text-secondary"
                title={t('twofa.title')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-hover transition-colors text-secondary hover:text-danger"
                title={t('nav.logout')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
              <TwoFaModal open={twoFaOpen} onClose={() => setTwoFaOpen(false)} />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-bg to-bg2">{children}</main>
      </div>
    </div>
  );
}
